import React, { useState, useEffect, useRef } from 'react'
import { Plus, MessageSquare, CheckCircle, Clock, AlertTriangle, TrendingUp } from 'lucide-react'
import DataTable from '../../../components/DataTable/DataTable'
import Breadcrumb from '../../../components/Common/Breadcrumb'
import ConfirmationModal from '../../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import projectCommunicationService from '../../../services/projectCommunicationService'
import '../../../components/ConfirmationModal/ConfirmationModal.css'

const PRIORITY_COLORS = {
  Low: '#10B981',
  Medium: '#F59E0B',
  High: '#EF4444',
  Urgent: '#DC2626'
}

const STATUS_COLORS = {
  Open: '#6A4CA2',
  'In Progress': '#222448',
  Resolved: '#10B981',
  Closed: '#5B6478'
}

const ProjectCommunicationList = () => {
  const navigate = useNavigate()
  const [selectedRows, setSelectedRows] = useState([])
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({ total: 0, open: 0, inProgress: 0, resolved: 0, closed: 0 })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [listRes, statsRes] = await Promise.all([
        projectCommunicationService.getAll({ limit: 1000 }),
        projectCommunicationService.getStats()
      ])

      if (listRes?.success) {
        const items = listRes.communications || []
        setData(items.map((c) => ({ ...c, id: c._id })))
        if (items.length === 0 && !hasShownEmptyToastRef.current) {
          hasShownEmptyToastRef.current = true
          toast.info('No project communications yet. Create your first message!')
        }
      } else {
        toast.error(listRes?.message || 'Failed to load communications')
      }

      if (statsRes?.success) {
        setStats(statsRes.data || {})
      }
    } catch (error) {
      console.error(error)
      toast.error('Failed to load communications')
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
  }

  const badge = (label, color) => (
    <span
      className="inline-flex items-center px-2.5 py-0.5 text-xs font-medium"
      style={{
        backgroundColor: `${color}20`,
        color,
        border: `1px solid ${color}40`
      }}
    >
      {label}
    </span>
  )

  const columns = [
    {
      key: 'communicationNumber',
      label: 'Comm #',
      sortable: true,
      render: (value) => value || 'N/A'
    },
    {
      key: 'subject',
      label: 'Subject',
      sortable: true,
      render: (value) => (
        <div style={{ maxWidth: '260px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {value || 'N/A'}
        </div>
      )
    },
    {
      key: 'project',
      label: 'Project',
      sortable: true,
      render: (_, row) => row.project?.projectName || 'N/A'
    },
    {
      key: 'communicationType',
      label: 'Type',
      sortable: true,
      render: (value) => value || 'Internal'
    },
    {
      key: 'priority',
      label: 'Priority',
      sortable: true,
      render: (value) => badge(value || 'Medium', PRIORITY_COLORS[value] || PRIORITY_COLORS.Medium)
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (value) => badge(value || 'Open', STATUS_COLORS[value] || STATUS_COLORS.Open)
    },
    {
      key: 'author',
      label: 'Author',
      sortable: true,
      render: (_, row) => row.author?.name || row.author?.userEmail || '—'
    },
    {
      key: 'replies',
      label: 'Replies',
      sortable: false,
      render: (_, row) => (Array.isArray(row.replies) ? row.replies.length : 0)
    },
    {
      key: 'createdAt',
      label: 'Created',
      sortable: true,
      render: (value) => formatDate(value)
    }
  ]

  const breadcrumbItems = [
    { label: 'Projects', path: '/projects', onClick: () => navigate('/projects') },
    { label: 'Transactions', path: '/projects?tab=transactions', onClick: () => navigate('/projects?tab=transactions') },
    { label: 'Project Communication', path: '/projects/communications', isCurrent: true }
  ]

  return (
    <div className="sales-dashboard">
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="page-header">
        <div className="page-title">
          <h1>Project Communication</h1>
          <p>Team collaboration and client communication threads</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn-primary" onClick={() => navigate('/projects/communications/new')}>
            <Plus size={16} />
            New Communication
          </button>
        </div>
      </div>

      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon"><MessageSquare size={16} /></div>
            <div className="metric-trend positive"><TrendingUp size={12} /><span>All</span></div>
          </div>
          <div className="metric-value">{stats.total || 0}</div>
          <div className="metric-title">Total Threads</div>
          <div className="metric-subtitle">Active records</div>
        </div>
        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon"><AlertTriangle size={16} /></div>
            <div className="metric-trend neutral"><TrendingUp size={12} /><span>Open</span></div>
          </div>
          <div className="metric-value">{stats.open || 0}</div>
          <div className="metric-title">Open</div>
          <div className="metric-subtitle">Needs attention</div>
        </div>
        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon"><Clock size={16} /></div>
            <div className="metric-trend neutral"><TrendingUp size={12} /><span>Active</span></div>
          </div>
          <div className="metric-value">{stats.inProgress || 0}</div>
          <div className="metric-title">In Progress</div>
          <div className="metric-subtitle">Being handled</div>
        </div>
        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon"><CheckCircle size={16} /></div>
            <div className="metric-trend positive"><TrendingUp size={12} /><span>Done</span></div>
          </div>
          <div className="metric-value">{(stats.resolved || 0) + (stats.closed || 0)}</div>
          <div className="metric-title">Resolved / Closed</div>
          <div className="metric-subtitle">Completed threads</div>
        </div>
      </div>

      <div className="dashboard-content">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onEdit={(row) => navigate(`/projects/communications/edit/${row._id || row.id}`)}
          onView={(row) => navigate(`/projects/communications/view/${row._id || row.id}`)}
          onDelete={(row) => {
            setDeleteItem(row)
            setShowDeleteModal(true)
          }}
          selectedRows={selectedRows}
          onSelectionChange={setSelectedRows}
          searchPlaceholder="Search communications..."
          emptyMessage="No communications found."
          noCard={true}
        />
      </div>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={async () => {
          if (!deleteItem) return
          try {
            const res = await projectCommunicationService.remove(deleteItem._id || deleteItem.id)
            if (res.success) {
              toast.success('Communication deleted')
              loadData()
            } else {
              toast.error(res.message || 'Delete failed')
            }
          } catch {
            toast.error('Failed to delete')
          } finally {
            setShowDeleteModal(false)
            setDeleteItem(null)
          }
        }}
        title="Delete Communication"
        message={`Delete "${deleteItem?.subject || deleteItem?.communicationNumber || ''}"?`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  )
}

export default ProjectCommunicationList
